import { message } from 'antd';
import { useEffect, useState, useCallback } from 'react';
import { getExamInputs } from '../../../services/exam';
import { getAsset } from '../../../services/general';
import { consultationService } from '../../../services/medicalConsultation';

const useSignatureData = (
  admissionPatientId,
  admissionPatientExamId,
  t,
) => {
  const [loading, setLoading] = useState({
    header: false,
    exam: false,
  });
  const [headerData, setHeaderData] = useState();
  const [examData, setExamData] = useState();
  const [extraExams, setExtraExams] = useState([]);

  const handleLoading = (signal, status) => {
    if (signal === 'start') {
      setLoading(loading => ({ ...loading, [status]: true }));
    } else {
      setLoading(loading => ({ ...loading, [status]: false }));
    }
  };

  const handleMessage = (content, state) => {
    if (state === 'success') {
      message.success({
        content,
        style: {
          marginTop: '40vh',
        },
      });
    } else {
      message.error({
        content,
        style: {
          marginTop: '40vh',
        },
      });
    }
  };

  const fetchPatientExams = useCallback(async () => {
    try {
      handleLoading('start', 'header');
      const [response] = await consultationService.getPatientData({
        admissionPatientId,
        admissionPatientExamId,
      });

      setHeaderData(response);

      const results = [];
      for await (let extraExamEntry of (response.extra_exam || [])) {
        const extraAdmissionPatientExamId = extraExamEntry?.admission_patient_exam_id;
        const editable = extraExamEntry?.editable !== false;
        try {
          if (extraAdmissionPatientExamId && extraAdmissionPatientExamId !== admissionPatientExamId) {
            const result = await getExamInputs({
              admissionPatientExamId: extraAdmissionPatientExamId
            });

            if (result) {
              const finalResult = await serializeExamData(result);
              results.push({ ...finalResult, admissionPatientExamId: extraAdmissionPatientExamId, editable });
            }
          }
        } catch (error) {
          console.error(`Error interno: ${error}`);
          handleMessage('error', `Error al traer datos del ${t('reservations.signatureHook.patient').toLowerCase()}: ${error}`);
        }
      }
      setExtraExams(results);
      handleLoading('end', 'header');
    } catch (error) {
      console.error(`Error al traer datos del ${t('reservations.signatureHook.patient').toLowerCase()}: ${error}`);
      handleMessage('error', `Error al traer datos del ${t('reservations.signatureHook.patient').toLowerCase()}: ${error}`);
      handleLoading('end', 'header');
    }
  }, [admissionPatientId, admissionPatientExamId, t]);

  const serializeExamData = async (response) => {
    let finalResponse = response;
    if (finalResponse['procedures']) {
      finalResponse['procedures'] = await Promise.all(
        finalResponse['procedures'].map(async (procedure) => {
          if (procedure['input_type'] === 'file') {
            return {
              ...procedure,
              file_data: await getFile(procedure),
            };
          }
          return procedure;
        })
      );
      finalResponse.procedures = finalResponse.procedures.sort((a, b) => a.order - b.order);
    }
    if (finalResponse.measurements) {
      finalResponse.measurements = finalResponse.measurements.sort((a, b) => a.order - b.order);
    }
    if (finalResponse['surveys']) {
      const surveysWithQuestions = {};
      finalResponse['surveys'].forEach(survey => {
        surveysWithQuestions[survey.id] = {
          ...survey,
          questions: [],
        }
      });
      finalResponse['questions'].forEach(question => {
        surveysWithQuestions[question.survey_id].questions.push(question);
      });
      Object.keys(surveysWithQuestions).forEach(surveyId => {
        surveysWithQuestions[surveyId].questions = surveysWithQuestions[surveyId].questions.sort((a, b) => a.order - b.order);
      })
      finalResponse['surveys'] = Object.values(surveysWithQuestions);
    }

    return response;
  }

  const fetchExamData = useCallback(async () => {
    if (admissionPatientExamId) {
      try {
        handleLoading('start', 'exam');
        const response = await getExamInputs({ admissionPatientExamId });
        if (response.state) {
          const finalResponse = await serializeExamData(response);
          setExamData({ ...finalResponse, admissionPatientExamId: admissionPatientExamId });
        }
        handleLoading('end', 'exam');
      } catch (error) {
        console.error(`Error al traer datos del examen: ${error}`);
        handleMessage('error', `Error al traer datos del examen: ${error}`);
        handleLoading('end', 'exam');
      }
    }
  }, [admissionPatientExamId]);

  const getFile = async (procedure) => {
    if (procedure.result_id) {
      const response = await getAsset({
        field: 'document',
        id: procedure.result_id,
        type: 'AdmissionProcedureResult',
      });

      return response;
    }
  };

  useEffect(() => {
    fetchPatientExams();
  }, [fetchPatientExams])

  useEffect(() => {
    fetchExamData();
  }, [fetchExamData]);

  return {
    loading,
    headerData,
    examData,
    extraExams,
  };
};

export default useSignatureData;
